import type { SxProps, Theme } from "@mui/material";

/**
 * The listing table's right-hand controls (pin, eye, show-on-map, menu) stay
 * put while the data columns scroll horizontally. Each frozen cell is sticky
 * with a `right` offset equal to the width of everything frozen to its right.
 */
export const ICON_COL = 40;
export const MENU_COL = 48;
/** Keeps the last button clear of an overlay scrollbar at the pane's edge. */
export const EDGE_PAD = 16;

export const FROZEN_RIGHT = {
  menu: 0,
  locate: MENU_COL + EDGE_PAD,
  eye: MENU_COL + EDGE_PAD + ICON_COL,
  pin: MENU_COL + EDGE_PAD + ICON_COL * 2,
} as const;

/** How many trailing columns are frozen (used for folder rows' colSpan). */
export const FROZEN_COLUMN_COUNT = 4;

/**
 * A frozen body cell. Its background is inherited from the row, which sets an
 * opaque colour for every state, so scrolled content never shows through.
 */
export const frozenCellSx = (right: number, edgePad = false): SxProps<Theme> => ({
  position: "sticky",
  right,
  zIndex: 1,
  backgroundColor: "inherit",
  width: (right === FROZEN_RIGHT.menu ? MENU_COL : ICON_COL) + (edgePad ? EDGE_PAD : 0),
  minWidth: (right === FROZEN_RIGHT.menu ? MENU_COL : ICON_COL) + (edgePad ? EDGE_PAD : 0),
  pr: edgePad ? `${EDGE_PAD}px` : 0,
});

/** A frozen header cell: sticky to the top (MUI) and to the right (ours). */
export const frozenHeadSx = (right: number, edgePad = false): SxProps<Theme> => ({
  position: "sticky",
  right,
  zIndex: 3,
  backgroundColor: "#fff",
  width: (right === FROZEN_RIGHT.menu ? MENU_COL : ICON_COL) + (edgePad ? EDGE_PAD : 0),
  minWidth: (right === FROZEN_RIGHT.menu ? MENU_COL : ICON_COL) + (edgePad ? EDGE_PAD : 0),
});

/** Opaque row colours (translucent tints would let scrolled cells show through frozen ones). */
export const ROW_BG = {
  base: "#ffffff",
  hover: "#f3f5f7",
  highlightProject: "#e0f8f3",
  highlightProjectHover: "#d2f3ea",
  highlightFolder: "#fdf2dc",
  highlightFolderHover: "#fbe9c6",
  dropTarget: "#e3f9f4",
} as const;
